import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CountryCode } from 'libphonenumber-js';
import { EMPTY, Observable, catchError, finalize, map, of, switchMap, take } from 'rxjs';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  OrganizationApiService,
  type CreateOrganizationBody,
  type OrganizationRecord,
  type UpdateOrganizationBody,
} from 'src/app/core/services/organization-api.service';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { IntlPhoneFieldComponent } from 'src/app/shared/components/intl-phone-field/intl-phone-field.component';
import {
  dialStringForCountry,
  formatPhoneInternationalDisplay,
  normalizeNationalDigits,
  splitPhoneForForm,
  validateNationalPhone,
} from 'src/app/shared/utils/international-phone';
import { normalizeGstin, normalizePan, validateIndianGstin, validateIndianPan } from 'src/app/shared/utils/india-tax-ids';
import { validateEmailFormat } from 'src/app/shared/utils/email-format';
import { BrandLogoService } from 'src/app/core/services/brand-logo.service';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';

const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';
const LOGO_TYPES = new Set(LOGO_ACCEPT.split(','));
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_RE = /^\d{9,18}$/;
const UPI_RE = /^[\w.-]{2,}@[A-Za-z]{2,}$/;
const SAC_RE = /^\d{4,8}$/;
const DEFAULT_PAYMENT_TERMS_DAYS = 15;
const MAX_PAYMENT_TERMS_DAYS = 120;

type FieldErrors = Partial<Record<keyof OrganizationForm, string>>;

type OrganizationForm = {
  organization_name: string;
  legal_name: string;
  gst_no: string;
  pan_no: string;
  email: string;
  address: string;
  city: string;
  country: string;
  pincode: string;
  bank_name: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_ifsc: string;
  upi_id: string;
  sac_code: string;
  payment_terms_days: string;
};

@Component({
  selector: 'app-organization-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, IconModule, IntlPhoneFieldComponent, HasPermissionPipe],
  templateUrl: './organization-settings.component.html',
})
export class OrganizationSettingsComponent implements OnInit, OnDestroy {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly organizationApi = inject(OrganizationApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly brandLogo = inject(BrandLogoService);

  organization: OrganizationRecord | null = null;
  form: OrganizationForm = this.emptyForm();
  phoneIso: CountryCode = 'IN';
  phoneNational = '';
  phoneError = '';
  loading = false;
  saving = false;
  editMode = false;
  loadError = '';
  saveError = '';
  fieldErrors: FieldErrors = {};

  readonly logoAccept = LOGO_ACCEPT;
  logoFile: File | null = null;
  logoPreviewUrl: string | null = null;
  removeLogoRequested = false;
  logoError = '';

  ngOnInit(): void {
    this.loadOrganization();
  }

  ngOnDestroy(): void {
    this.resetLogoState();
  }

  get currentLogoUrl(): string | null {
    if (this.logoPreviewUrl) return this.logoPreviewUrl;
    return this.removeLogoRequested ? null : (this.organization?.logo_url ?? null);
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    input.value = '';

    // Mirrors the backend limits so obvious mistakes fail before the upload.
    if (!LOGO_TYPES.has(file.type)) {
      this.logoError = 'Choose a PNG, JPEG, WebP or SVG image.';
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      this.logoError = 'The logo must be 2 MB or smaller.';
      return;
    }
    this.revokeLogoPreview();
    this.logoFile = file;
    this.logoPreviewUrl = URL.createObjectURL(file);
    this.removeLogoRequested = false;
    this.logoError = '';
  }

  removeLogo(): void {
    this.logoFile = null;
    this.revokeLogoPreview();
    this.removeLogoRequested = !!this.organization?.logo_url;
  }

  get hasOrganization(): boolean {
    return !!this.organization;
  }

  get pageTitle(): string {
    return this.hasOrganization
      ? 'Organization Details'
      : 'Add Organization Details';
  }

  get pageSubtitle(): string {
    return this.hasOrganization
      ? 'View and update the platform organization profile and contact information.'
      : 'Add the platform organization profile once. You can edit it later, but cannot add a second organization.';
  }

  loadOrganization(): void {
    this.loadError = '';
    this.loading = true;
    this.organizationApi
      .getOrganization()
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (env) => {
          this.organization = env.data ?? null;
          this.editMode = false;
        },
        error: (err) => {
          this.loadError =
            resolveApiErrorMessage(err) || 'Could not load organization details.';
        },
      });
  }

  startAdd(): void {
    this.resetLogoState();
    this.saveError = '';
    this.fieldErrors = {};
    this.phoneError = '';
    this.phoneIso = 'IN';
    this.phoneNational = '';
    this.form = this.emptyForm();
    this.editMode = true;
  }

  startEdit(): void {
    if (!this.organization) return;
    this.resetLogoState();
    this.saveError = '';
    this.fieldErrors = {};
    this.phoneError = '';
    this.form = this.recordToForm(this.organization);
    this.applyPhoneFromRecord(this.organization);
    this.editMode = true;
  }

  cancelEdit(): void {
    this.resetLogoState();
    this.editMode = false;
    this.saveError = '';
    this.fieldErrors = {};
    this.phoneError = '';
    if (this.organization) {
      this.form = this.recordToForm(this.organization);
      this.applyPhoneFromRecord(this.organization);
    } else {
      this.phoneIso = 'IN';
      this.phoneNational = '';
      this.form = this.emptyForm();
    }
  }

  submit(ev: Event): void {
    ev.preventDefault();
    this.saveError = '';
    this.fieldErrors = {};
    this.phoneError = '';

    if (!this.validateForm()) {
      return;
    }

    const body = this.formToBody();
    const isUpdate = !!this.organization;
    this.saving = true;

    const request$ = isUpdate
      ? this.organizationApi.updateOrganization(this.organization!.id, body)
      : this.organizationApi.createOrganization(body as CreateOrganizationBody);

    request$
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        switchMap((env) => this.saveThenApplyLogo(env.data ?? null, env.message, isUpdate)),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (message) => {
          this.editMode = false;
          this.resetLogoState();
          this.snackBar.open(message, 'Dismiss', { duration: 5000 });
        },
        error: (err) => {
          this.saveError =
            resolveApiErrorMessage(err) || 'Could not save organization details.';
        },
      });
  }

  // The details are already saved when this runs, so a logo failure must not
  // discard them: keep the record, stay in edit mode and report both facts.
  private saveThenApplyLogo(
    saved: OrganizationRecord | null,
    apiMessage: string | undefined,
    isUpdate: boolean,
  ): Observable<string> {
    const message =
      apiMessage ??
      (isUpdate
        ? 'Organization updated successfully.'
        : 'Organization created successfully.');
    if (!saved) {
      this.organization = null;
      return of(message);
    }
    this.organization = saved;
    return this.applyLogoChange(saved).pipe(
      map((record) => {
        this.organization = record;
        // Once per save, with the final record, so the header never flashes the pre-logo value.
        this.brandLogo.set(record.logo_url ?? null);
        return message;
      }),
      catchError((err) => {
        this.saveError = `Details were saved, but the logo could not be updated: ${
          resolveApiErrorMessage(err) || 'Unknown error.'
        }`;
        return EMPTY;
      }),
    );
  }

  private applyLogoChange(record: OrganizationRecord): Observable<OrganizationRecord> {
    if (this.logoFile) {
      return this.organizationApi
        .uploadLogo(record.id, this.logoFile)
        .pipe(assertSuccessEnvelope(), map((env) => env.data ?? record));
    }
    if (this.removeLogoRequested) {
      return this.organizationApi
        .removeLogo(record.id)
        .pipe(assertSuccessEnvelope(), map((env) => env.data ?? record));
    }
    return of(record);
  }

  private revokeLogoPreview(): void {
    if (this.logoPreviewUrl) {
      URL.revokeObjectURL(this.logoPreviewUrl);
    }
    this.logoPreviewUrl = null;
  }

  private resetLogoState(): void {
    this.revokeLogoPreview();
    this.logoFile = null;
    this.removeLogoRequested = false;
    this.logoError = '';
  }

  display(value: string | null | undefined): string {
    const v = (value ?? '').trim();
    return v || '—';
  }

  displayPhone(record: OrganizationRecord | null): string {
    if (!record) return '—';
    return formatPhoneInternationalDisplay(
      record.mobile_number,
      record.country_code,
      null,
    );
  }

  onPhoneNationalChange(): void {
    this.phoneError = '';
  }

  fieldError(field: keyof OrganizationForm): string {
    return this.fieldErrors[field] ?? '';
  }

  clearFieldError(field: keyof OrganizationForm): void {
    if (!this.fieldErrors[field]) return;
    const { [field]: _removed, ...rest } = this.fieldErrors;
    this.fieldErrors = rest;
  }

  private validateForm(): boolean {
    this.fieldErrors = this.collectFieldValidationErrors();
    this.validatePhoneField();
    return Object.keys(this.fieldErrors).length === 0 && !this.phoneError;
  }

  private collectFieldValidationErrors(): Partial<Record<keyof OrganizationForm, string>> {
    const errors: Partial<Record<keyof OrganizationForm, string>> = {};
    const trim = (value: string) => value.trim();

    const requiredFields: Array<{ key: keyof OrganizationForm; label: string }> = [
      { key: 'organization_name', label: 'Organization name' },
      { key: 'legal_name', label: 'Legal name' },
      { key: 'address', label: 'Street address' },
      { key: 'city', label: 'City' },
      { key: 'country', label: 'Country' },
      { key: 'pincode', label: 'PIN code' },
    ];

    for (const field of requiredFields) {
      if (!trim(this.form[field.key])) {
        errors[field.key] = `${field.label} is required.`;
      }
    }

    this.applyGstValidation(errors);
    this.applyPanValidation(errors);
    this.applyEmailValidation(errors, trim);
    this.applyBankValidation(errors);
    this.applyPaymentTermsValidation(errors);

    return errors;
  }

  private applyGstValidation(
    errors: Partial<Record<keyof OrganizationForm, string>>,
  ): void {
    if (!this.form.gst_no.trim()) {
      errors.gst_no = 'GST number is required.';
      return;
    }
    const gstErr = validateIndianGstin(this.form.gst_no);
    if (gstErr) {
      errors.gst_no = gstErr;
    }
  }

  private applyPanValidation(
    errors: Partial<Record<keyof OrganizationForm, string>>,
  ): void {
    if (!this.form.pan_no.trim()) {
      errors.pan_no = 'PAN number is required.';
      return;
    }
    const panErr = validateIndianPan(this.form.pan_no);
    if (panErr) {
      errors.pan_no = panErr;
    }
  }

  private applyEmailValidation(
    errors: Partial<Record<keyof OrganizationForm, string>>,
    trim: (value: string) => string,
  ): void {
    const email = trim(this.form.email);
    if (!email) {
      errors.email = 'Email ID is required.';
      return;
    }
    const emailErr = validateEmailFormat(email);
    if (emailErr) {
      errors.email = emailErr;
    }
  }

  private applyBankValidation(errors: FieldErrors): void {
    const ifsc = this.form.bank_ifsc.trim().toUpperCase();
    if (ifsc && !IFSC_RE.test(ifsc)) {
      errors.bank_ifsc =
        'IFSC must be 11 characters: 4 letters, a zero, then 6 letters or digits.';
    }
    const account = this.form.bank_account_number.trim();
    if (account && !ACCOUNT_RE.test(account)) {
      errors.bank_account_number = 'Account number must be 9 to 18 digits.';
    }
    const upi = this.form.upi_id.trim();
    if (upi && !UPI_RE.test(upi)) {
      errors.upi_id = 'Enter a valid UPI ID, for example name@bank.';
    }
    const sac = this.form.sac_code.trim();
    if (sac && !SAC_RE.test(sac)) {
      errors.sac_code = 'Enter a 4 to 8 digit HSN/SAC code, for example 998314.';
    }
  }

  private applyPaymentTermsValidation(errors: FieldErrors): void {
    const raw = String(this.form.payment_terms_days ?? '').trim();
    if (!raw) return;
    const days = Number(raw);
    if (!Number.isInteger(days) || days < 0 || days > MAX_PAYMENT_TERMS_DAYS) {
      errors.payment_terms_days = `Payment terms must be a whole number of days from 0 to ${MAX_PAYMENT_TERMS_DAYS}.`;
    }
  }

  private validatePhoneField(): void {
    const mobileDigits = this.normalizeMobileForApi();
    if (!mobileDigits) {
      this.phoneError = 'Phone number is required';
      return;
    }
    this.phoneError =
      validateNationalPhone(this.phoneIso, this.phoneNational) ?? '';
  }

  private normalizeMobileForApi(): string {
    let digits = normalizeNationalDigits(this.phoneNational);
    if (this.phoneIso === 'IN' && digits.length === 11 && digits.startsWith('0')) {
      digits = digits.slice(1);
    }
    return digits;
  }

  private applyPhoneFromRecord(record: OrganizationRecord): void {
    const split = splitPhoneForForm(record.country_code, record.mobile_number, 'IN');
    this.phoneIso = split.iso;
    this.phoneNational = split.national;
  }

  private emptyForm(): OrganizationForm {
    return {
      organization_name: '',
      legal_name: '',
      gst_no: '',
      pan_no: '',
      email: '',
      address: '',
      city: '',
      country: '',
      pincode: '',
      bank_name: '',
      bank_account_name: '',
      bank_account_number: '',
      bank_ifsc: '',
      upi_id: '',
      sac_code: '',
      payment_terms_days: String(DEFAULT_PAYMENT_TERMS_DAYS),
    };
  }

  private recordToForm(record: OrganizationRecord): OrganizationForm {
    return {
      organization_name: record.organization_name ?? '',
      legal_name: record.legal_name ?? '',
      gst_no: record.gst_no ?? '',
      pan_no: record.pan_no ?? '',
      email: record.email ?? '',
      address: record.address ?? '',
      city: record.city ?? '',
      country: record.country ?? '',
      pincode: record.pincode ?? '',
      bank_name: record.bank_name ?? '',
      bank_account_name: record.bank_account_name ?? '',
      bank_account_number: record.bank_account_number ?? '',
      bank_ifsc: record.bank_ifsc ?? '',
      upi_id: record.upi_id ?? '',
      sac_code: record.sac_code ?? '',
      payment_terms_days: String(
        record.payment_terms_days ?? DEFAULT_PAYMENT_TERMS_DAYS,
      ),
    };
  }

  private formToBody(): UpdateOrganizationBody {
    const trim = (v: string) => v.trim();
    return {
      organization_name: trim(this.form.organization_name),
      legal_name: trim(this.form.legal_name),
      gst_no: normalizeGstin(this.form.gst_no),
      pan_no: normalizePan(this.form.pan_no),
      country_code: dialStringForCountry(this.phoneIso),
      mobile_number: this.normalizeMobileForApi(),
      email: trim(this.form.email),
      address: trim(this.form.address),
      city: trim(this.form.city),
      country: trim(this.form.country),
      pincode: trim(this.form.pincode),
      bank_name: trim(this.form.bank_name),
      bank_account_name: trim(this.form.bank_account_name),
      bank_account_number: trim(this.form.bank_account_number),
      bank_ifsc: trim(this.form.bank_ifsc).toUpperCase(),
      upi_id: trim(this.form.upi_id),
      sac_code: trim(this.form.sac_code),
      payment_terms_days: this.paymentTermsDays(),
    };
  }

  private paymentTermsDays(): number {
    const raw = String(this.form.payment_terms_days ?? '').trim();
    return raw ? Number(raw) : DEFAULT_PAYMENT_TERMS_DAYS;
  }
}
